// =============================================================================
// Shared video processing pipeline.
//
// Movido textualmente desde pages/OrderDetail.jsx para que tanto OrderDetail
// como DiagnosisSection (botones Cámara/Galería del diagnóstico) usen el MISMO
// pipeline probado: validación duración 20s, transcodeo H.264/MP4 720p vía
// MediaRecorder+canvas.captureStream con fallback WebM, mezcla de audio,
// miniatura, cap de tamaño final 11MB.
//
// Ajuste único: MAX_ORIGINAL_MB sube de 60 a 120 para aceptar videos nativos
// pesados de Samsung/Android/iPhone (1080p/60fps o 4K) hasta 20s antes de que
// el transcodeo los reduzca.
// =============================================================================

export const MAX_VIDEO_DURATION_S = 20;
export const MAX_ORIGINAL_MB = 120;  // raw file ceiling (teléfonos modernos pueden grabar 70-100MB en 20s @ 4K)
export const MAX_FINAL_MB = 11;      // hard cap on final upload size (Mongo doc friendly)
export const COMPRESS_ABOVE_MB = 3;  // try to compress anything > 3MB
export const TARGET_MAX_SIDE = 720;

export function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(new Error("No se pudo leer el archivo"));
    fr.readAsDataURL(blob);
  });
}

export function loadVideoMeta(url) {
  return new Promise((resolve, reject) => {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.muted = true;
    v.playsInline = true;
    let done = false;
    const cleanup = () => {
      v.onloadedmetadata = null;
      v.onerror = null;
    };
    v.onloadedmetadata = () => {
      if (done) return;
      done = true;
      const info = { duration: v.duration, width: v.videoWidth, height: v.videoHeight };
      cleanup();
      resolve(info);
    };
    v.onerror = () => {
      if (done) return;
      done = true;
      cleanup();
      reject(new Error("No se pudo leer el video. Formato no soportado."));
    };
    v.src = url;
    // Safety timeout
    setTimeout(() => {
      if (!done) {
        done = true;
        cleanup();
        reject(new Error("Tiempo agotado al leer el video."));
      }
    }, 15000);
  });
}

export async function captureThumbnail(url, atSec = 1, maxSide = 480) {
  return new Promise((resolve, reject) => {
    const v = document.createElement("video");
    v.preload = "auto";
    v.muted = true;
    v.playsInline = true;
    v.crossOrigin = "anonymous";
    let done = false;
    const finish = (val, err) => {
      if (done) return;
      done = true;
      v.onloadeddata = null;
      v.onseeked = null;
      v.onerror = null;
      if (err) reject(err); else resolve(val);
    };
    v.onloadeddata = () => {
      try {
        v.currentTime = Math.min(Math.max(0.1, atSec), Math.max(0.1, (v.duration || 1) - 0.05));
      } catch (e) {
        finish(null, e);
      }
    };
    v.onseeked = () => {
      try {
        let w = v.videoWidth || 640;
        let h = v.videoHeight || 360;
        const scale = Math.min(1, maxSide / Math.max(w, h));
        w = Math.round(w * scale);
        h = Math.round(h * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(v, 0, 0, w, h);
        finish(canvas.toDataURL("image/jpeg", 0.72));
      } catch (e) {
        finish(null, e);
      }
    };
    v.onerror = () => finish(null, new Error("No se pudo generar la miniatura"));
    v.src = url;
    setTimeout(() => finish(null, new Error("Tiempo agotado al generar miniatura")), 15000);
  });
}

export const CONVERT_FAILED_MSG = "No pudimos convertir este video. Intenta nuevamente o selecciona otro archivo.";

const VIDEO_TAGS = [
  ["avc", ["avc1", "avc3"]],
  ["hevc", ["hvc1", "hev1", "V_MPEGH/ISO/HEVC"]],
  ["vp9", ["vp09", "V_VP9"]],
  ["vp8", ["V_VP8"]],
  ["av1", ["av01", "V_AV1"]],
];
const AUDIO_TAGS = [
  ["aac", ["mp4a", "A_AAC"]],
  ["opus", ["Opus", "dOps", "A_OPUS"]],
  ["vorbis", ["A_VORBIS"]],
];
const bytesToStr = (u8) => { let r = ""; for (let i = 0; i < u8.length; i++) r += String.fromCharCode(u8[i]); return r; };
const findTag = (txt, table) => (table.find(([, tags]) => tags.some((t) => txt.includes(t))) || [null])[0];

// Recorre las cajas top-level de MP4/MOV: detecta truncado y devuelve el contenido de 'moov'.
async function walkIsoBoxes(blob) {
  let off = 0;
  let moov = null;
  let truncated = false;
  for (let n = 0; off < blob.size && n < 20000; n++) {
    const h = new DataView(await blob.slice(off, off + 16).arrayBuffer());
    if (h.byteLength < 8) { truncated = true; break; }
    let size = h.getUint32(0);
    const type = bytesToStr(new Uint8Array(h.buffer, 4, 4));
    if (size === 1) {
      if (h.byteLength < 16) { truncated = true; break; }
      size = h.getUint32(8) * 4294967296 + h.getUint32(12);
    } else if (size === 0) size = blob.size - off;
    if (size < 8 || off + size > blob.size) { truncated = true; break; }
    if (type === "moov") moov = new Uint8Array(await blob.slice(off, off + Math.min(size, 8 * 1024 * 1024)).arrayBuffer());
    off += size;
  }
  return { moov, truncated };
}

// Inspecciona los bytes reales para conocer contenedor, códecs y completitud,
// sin confiar en la extensión ni en el MIME declarado.
export async function sniffVideo(blob) {
  const head = new Uint8Array(await blob.slice(0, 64 * 1024).arrayBuffer());
  const info = { container: "unknown", brand: "", codec: null, audio: null, complete: false, size: blob.size };
  if (head[0] === 0x1a && head[1] === 0x45 && head[2] === 0xdf && head[3] === 0xa3) {
    const txt = bytesToStr(head);
    return { ...info, container: "webm", codec: findTag(txt, VIDEO_TAGS), audio: findTag(txt, AUDIO_TAGS), complete: true };
  }
  if (bytesToStr(head.slice(4, 8)) !== "ftyp") return info;
  info.brand = bytesToStr(head.slice(8, 12));
  info.container = info.brand === "qt  " ? "mov" : "mp4";
  const { moov, truncated } = await walkIsoBoxes(blob);
  if (moov) {
    const txt = bytesToStr(moov);
    info.codec = findTag(txt, VIDEO_TAGS);
    info.audio = findTag(txt, AUDIO_TAGS);
  }
  info.complete = !!moov && !truncated;
  return info;
}

// Compatible con Android/Chrome, iPhone/Safari y escritorio sin conversión.
export function isPlayableEverywhere(info) {
  if (!info || info.size < 1024 || !info.complete) return false;
  if (info.container === "mp4") return info.codec === "avc" && (info.audio === null || info.audio === "aac");
  if (info.container === "webm") return info.codec === "vp8" || info.codec === "vp9";
  return false;
}

async function validateOutput(blob) {
  const info = await sniffVideo(blob);
  if (!isPlayableEverywhere(info)) return null;
  const type = info.container === "webm" ? "video/webm" : "video/mp4";
  const typed = blob.type === type ? blob : new Blob([blob], { type });
  const url = URL.createObjectURL(typed);
  try {
    const meta = await loadVideoMeta(url);
    if (!meta.width || !meta.height) return null;
  } catch (e) {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
  return { blob: typed, type };
}

// MP4 sólo con AAC (Opus dentro de MP4 no se reproduce en Safari/iOS). Si el navegador
// no graba AAC, MP4 H.264 sin audio; WebM queda como último recurso.
export function pickRecorderMime(withAudio = true) {
  if (typeof MediaRecorder === "undefined") return null;
  const candidates = [
    ...(withAudio ? ["video/mp4;codecs=avc1.42E01E,mp4a.40.2", "video/mp4;codecs=avc1,mp4a.40.2", "video/mp4;codecs=h264,aac"] : []),
    "video/mp4;codecs=avc1.42E01E",
    "video/mp4;codecs=avc1",
    "video/mp4;codecs=h264",
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
  ];
  for (const m of candidates) {
    try { if (MediaRecorder.isTypeSupported(m)) return m; } catch (e) {}
  }
  return "";
}

export async function transcodeVideo(url, meta, maxSide = TARGET_MAX_SIDE, onProgress) {
  if (typeof MediaRecorder === "undefined") {
    throw new Error("MediaRecorder no soportado");
  }
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.src = url;
    video.muted = false;
    video.playsInline = true;
    video.preload = "auto";
    // Use lower internal volume to reduce feedback but keep audio track live for capture
    try { video.volume = 0.0001; } catch (e) {}

    let recorder = null;
    let raf = null;
    let started = false;
    let errored = false;
    const cleanup = () => {
      if (raf) cancelAnimationFrame(raf);
      video.onloadedmetadata = null;
      video.onended = null;
      video.onerror = null;
    };
    const fail = (err) => {
      if (errored) return;
      errored = true;
      cleanup();
      try { recorder && recorder.state !== "inactive" && recorder.stop(); } catch (e) {}
      reject(err instanceof Error ? err : new Error(String(err || "Error de transcodificación")));
    };

    video.onloadedmetadata = () => {
      try {
        let w = video.videoWidth;
        let h = video.videoHeight;
        if (!w || !h) throw new Error("Dimensiones inválidas");
        const scale = Math.min(1, maxSide / Math.max(w, h));
        w = Math.max(2, Math.round((w * scale) / 2) * 2);
        h = Math.max(2, Math.round((h * scale) / 2) * 2);

        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        const canvasStream = canvas.captureStream(30);

        // Merge audio from video element if available
        let audioTrack = null;
        try {
          const vStream = typeof video.captureStream === "function"
            ? video.captureStream()
            : (typeof video.mozCaptureStream === "function" ? video.mozCaptureStream() : null);
          if (vStream) {
            const at = vStream.getAudioTracks()[0];
            if (at) audioTrack = at;
          }
        } catch (e) { /* audio optional */ }

        let mimeType = pickRecorderMime(!!audioTrack);
        // Si sólo hay MP4 sin AAC, se descarta el audio para no generar Opus-en-MP4.
        if (audioTrack && /^video\/mp4/.test(mimeType || "") && !/mp4a|aac/.test(mimeType)) audioTrack = null;
        const combined = new MediaStream();
        canvasStream.getVideoTracks().forEach((t) => combined.addTrack(t));
        if (audioTrack) combined.addTrack(audioTrack);

        const options = {};
        if (mimeType) options.mimeType = mimeType;
        // Target ~900 kbps video + 64 kbps audio -> ~7 MB per 20s
        options.videoBitsPerSecond = 900_000;
        if (audioTrack) options.audioBitsPerSecond = 64_000;

        recorder = new MediaRecorder(combined, options);

        const chunks = [];
        recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
        recorder.onstop = () => {
          cleanup();
          const type = recorder.mimeType || mimeType || "video/webm";
          const blob = new Blob(chunks, { type });
          resolve(blob);
        };
        recorder.onerror = (e) => fail(e.error || new Error("Error de grabación"));

        recorder.start(250);
        started = true;

        const draw = () => {
          if (video.paused || video.ended) return;
          try { ctx.drawImage(video, 0, 0, w, h); } catch (e) { /* ignore */ }
          if (onProgress && meta && meta.duration > 0) {
            onProgress(Math.min(1, video.currentTime / meta.duration));
          }
          raf = requestAnimationFrame(draw);
        };

        video.onended = () => {
          try { if (recorder && recorder.state !== "inactive") recorder.stop(); } catch (e) {}
        };

        video.play().then(() => {
          draw();
        }).catch(fail);
      } catch (e) {
        fail(e);
      }
    };
    video.onerror = () => fail(new Error("No se pudo cargar el video para transcodificar"));

    // Overall safety timeout
    setTimeout(() => {
      if (!started) fail(new Error("Tiempo agotado al iniciar la transcodificación"));
    }, 20000);
  });
}

export async function processVideoFile(file, onStatus) {
  if (file.size > MAX_ORIGINAL_MB * 1024 * 1024) {
    throw new Error(`El video es demasiado grande (${(file.size / 1024 / 1024).toFixed(0)} MB). Máx ${MAX_ORIGINAL_MB} MB. Intenta con uno más corto.`);
  }
  const url = URL.createObjectURL(file);
  try {
    onStatus && onStatus("Leyendo video…");
    const meta = await loadVideoMeta(url);
    if (!isFinite(meta.duration) || meta.duration <= 0) {
      throw new Error("No se pudo determinar la duración del video.");
    }
    if (meta.duration > MAX_VIDEO_DURATION_S + 0.5) {
      throw new Error(`El video dura ${meta.duration.toFixed(1)} s. Máximo permitido: ${MAX_VIDEO_DURATION_S} segundos.`);
    }

    onStatus && onStatus("Generando miniatura…");
    const thumbnail = await captureThumbnail(url, Math.min(1, meta.duration * 0.2)).catch(() => null);

    const original = await sniffVideo(file);
    const originalOk = isPlayableEverywhere(original);
    const shouldCompress = !originalOk || file.size > COMPRESS_ABOVE_MB * 1024 * 1024;
    let outputBlob = null;
    let mimeType = null;

    if (shouldCompress) {
      let converted = null;
      try {
        onStatus && onStatus("Convirtiendo a MP4 720p…");
        const compressed = await transcodeVideo(url, meta, TARGET_MAX_SIDE, (p) => {
          onStatus && onStatus(`Convirtiendo video… ${Math.round(p * 100)}%`);
        });
        if (compressed && compressed.size > 0) converted = await validateOutput(compressed);
      } catch (err) {
        console.warn("Transcodeo falló:", err);
      }
      if (converted && (!originalOk || converted.blob.size < file.size)) {
        outputBlob = converted.blob;
        mimeType = converted.type;
      }
    }
    if (!outputBlob) {
      // Nunca subir un original que no sea reproducible en todos los dispositivos.
      if (!originalOk) throw new Error(CONVERT_FAILED_MSG);
      mimeType = original.container === "webm" ? "video/webm" : "video/mp4";
      outputBlob = file.type === mimeType ? file : new Blob([file], { type: mimeType });
    }

    if (outputBlob.size > MAX_FINAL_MB * 1024 * 1024) {
      throw new Error(`El video pesa ${(outputBlob.size / 1024 / 1024).toFixed(1)} MB tras el procesamiento (máx ${MAX_FINAL_MB} MB). Intenta con un video más corto o de menor resolución.`);
    }

    onStatus && onStatus("Preparando envío…");
    const dataUrl = await blobToDataURL(outputBlob);
    return { dataUrl, thumbnail, duration: meta.duration, mimeType };
  } finally {
    URL.revokeObjectURL(url);
  }
}
